import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { of, Subscription } from "rxjs";
import { catchError } from "rxjs/operators";
import { PortfolioService } from "../../../portfolio/portfolio.service";

interface AssetRow {
  ticker: string;
  name: string;
  weight: number;
  sector: string;
  country: string;
  type: string;
}

@Component({
  selector: "app-exp-edit",
  standalone: true,
  imports: [FormsModule],
  templateUrl: "./edit-experience.component.html",
})
export class EditExperienceComponent implements OnInit, OnDestroy {
  portfolio: any = null;
  name = "";
  description = "";
  currency = "";
  benchmark = "";
  startDate = "";
  endDate = "";
  rebalancing = "";
  weighting = "";
  rfr: number | null = null;
  visibility = "Private";
  fxHedging = "None";

  assets: AssetRow[] = [];
  totalWeight = 0;
  toast: { text: string; id: number } | null = null;
  private toastId = 0;

  isLoading = true;
  private subs = new Subscription();
  private portfolioId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private service: PortfolioService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.route.parent?.paramMap.subscribe((pm) => {
        const id = Number(pm.get("id"));
        if (id && id !== this.portfolioId) {
          this.portfolioId = id;
          this.load(id);
        }
      }) ?? Subscription.EMPTY,
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private load(id: number): void {
    this.isLoading = true;
    this.subs.add(
      this.service
        .getItem(id)
        .pipe(catchError(() => of(null)))
        .subscribe((portfolio: any) => {
          this.portfolio = portfolio;
          if (!portfolio) {
            this.isLoading = false;
            return;
          }
          this.name = portfolio.name || "";
          this.description = portfolio.description || "";
          this.currency =
            portfolio.currency?.code || portfolio.currency_code || "—";
          this.benchmark = portfolio.benchmark || "—";
          this.startDate = portfolio.start_date || "";
          this.endDate = portfolio.end_date || "";
          this.rebalancing =
            portfolio.rebalancing_period || portfolio.rebalance || "Quarterly";
          this.weighting =
            portfolio.weight_scheme ||
            portfolio.weighting_scheme ||
            "Market cap";
          this.rfr = portfolio.rfr ?? null;
          this.visibility = portfolio.shared_by_me?.length
            ? "Shared"
            : "Private";
          this.fxHedging = portfolio.currency_hedging?.length
            ? "Partial"
            : "None";

          const symbols: any[] = portfolio.symbol_performance || [];
          const sec = portfolio.security_overviews || {};
          this.assets = symbols
            .filter((e) => e.Ticker !== "Portfolio")
            .map((e) => ({
              ticker: e.Ticker,
              name:
                sec[e.Ticker]?.company_name || sec[e.Ticker]?.name || e.Ticker,
              weight: e.Weight || 0,
              sector:
                sec[e.Ticker]?.sector?.name || sec[e.Ticker]?.sector_fs || "—",
              country:
                sec[e.Ticker]?.country?.name ||
                sec[e.Ticker]?.country_fs ||
                "—",
              type: sec[e.Ticker]?.security_type_equity || "SHARE",
            }))
            .sort((a, b) => b.weight - a.weight);
          this.totalWeight = this.assets.reduce((s, r) => s + r.weight, 0);
          this.isLoading = false;
        }),
    );
  }

  save(): void {
    const id = ++this.toastId;
    this.toast = { text: "Changes captured locally (demo).", id };
    setTimeout(() => {
      if (this.toast?.id === id) this.toast = null;
    }, 2200);
  }

  fmtPct(v: number | null | undefined, d = 1): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    return (v * 100).toFixed(d) + "%";
  }
}
